/**
 * Custom compose normalizers
 * Canonicalize frontend draft values for custom/* API seams.
 * @module api/normalizers/custom
 */

/**
 * Normalize album-tail/addition selections into the canonical compose payload string.
 *
 * Ownership:
 * - raw inbound UI field state may be string, object, or multi-select array;
 * - canonical compose shape is a comma+space joined string of explicitly selected titles;
 * - consumers (composables/views) should call this helper instead of embedding local album-tail shaping rules.
 *
 * @param {unknown} value - UI field value for album_tail
 * @returns {string} Canonical compose-ready album_tail string
 * @precondition Value may be null, string, object with title, or array of such objects.
 * @postcondition Returns titles in original selection order joined by `, `.
 * @postcondition Never widens selection with unselected dictionary entries.
 * @sideeffects None.
 */
export function normalizeComposeAlbumTailSelection(value) {
  if (value == null) return ""

  const toTitle = (entry) => {
    if (entry == null) return ""
    if (typeof entry === "string") return entry.trim()
    if (typeof entry === "object") {
      if (entry.title != null) return String(entry.title).trim()
      if (entry.label != null) return String(entry.label).trim()
      if (entry.name != null) return String(entry.name).trim()
    }
    return String(entry).trim()
  }

  if (typeof value === "string") {
    return value.trim()
  }

  if (Array.isArray(value)) {
    return value.map(toTitle).filter(Boolean).join(", ")
  }

  return toTitle(value)
}

/**
 * Normalize the raw custom/clean_box response into explicit UI semantics.
 *
 * Ownership:
 * - raw inbound backend payload may encode success as numeric/string `result`;
 * - canonical clean-box shape exposes explicit `ok`/`warning` flags plus message metadata;
 * - consumers must depend on normalized semantics instead of coercing `result` locally.
 *
 * @param {unknown} raw - Raw clean-box response payload from backend
 * @returns {{warning: boolean, message: string, ok: boolean, missingCount: number|null}}
 * Canonical clean-box outcome.
 * @postcondition Success is true only for raw `result === 1` or `result === "1"`.
 * @postcondition Warning has priority over success when backend requests confirmation.
 * @postcondition Invalid or missing counts normalize to `null`.
 * @sideeffects None.
 */
export function normalizeCleanBoxResponse(raw) {
  const payload = raw && typeof raw === "object" ? raw : {}

  const parsedMissingCount = Number(payload.missing_count)
  const missingCount = Number.isFinite(parsedMissingCount)
    ? Math.trunc(parsedMissingCount)
    : null

  const warning =
    payload.warning === true || payload.warning === 1 || payload.warning === "1"

  if (warning) {
    return {
      warning: true,
      message: String(payload.message || ""),
      ok: false,
      missingCount,
    }
  }

  return {
    warning: false,
    message: String(payload.message || ""),
    ok: payload.result === 1 || payload.result === "1",
    missingCount,
  }
}
